/**
 * imageCompressor.ts
 * Canvas-based JPEG image compression shared across all group/cover uploads.
 *
 * Typical result: 3-6 MB smartphone photo -> 100-350 KB (80% quality, max 1200px).
 * Use compressForCover() for group/event covers.
 * Use compressImage() for generic compression with custom params.
 */

/**
 * Compress an image File using an off-screen canvas.
 * @param file      - Source image file (any format the browser can decode)
 * @param maxWidth  - Maximum output width in pixels (default 1200)
 * @param quality   - JPEG quality 0-1 (default 0.80 = ~80 %)
 * @returns A new File with type image/jpeg, same base name but .jpg extension.
 *          Falls back to the original file on any error.
 */
export async function compressImage(
  file: File,
  maxWidth = 1200,
  quality = 0.8,
): Promise<File> {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      try {
        const scale = img.width > maxWidth ? maxWidth / img.width : 1;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) { URL.revokeObjectURL(url); resolve(file); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (!blob) { resolve(file); return; }
            const outName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
            resolve(new File([blob], outName, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          quality,
        );
      } catch {
        URL.revokeObjectURL(url);
        resolve(file);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // fallback: upload original
    };

    img.src = url;
  });
}

/**
 * Convenience wrapper: compress a group/event cover image.
 * Targets max 1200 px wide at 82 % quality (keeps crispness for banners).
 */
export const compressForCover = (file: File) => compressImage(file, 1200, 0.82);

/**
 * Convenience wrapper: compress an event thumbnail/card image.
 * Smaller target (800 px) for faster lightbox loads.
 */
export const compressForThumbnail = (file: File) => compressImage(file, 800, 0.78);
